"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Cpu, BrainCircuit, BookOpen, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tracks = [{
  title: "Uncompromising Privacy",
  description: "Your home data stays within your walls. We leverage HomeKit's end-to-end encryption to ensure your life remains private.",
  icon: Cpu,
  link: "/MozaMethodPage",
  animation: "group-hover:text-secondary group-hover:scale-110"
}, {
  title: "Refined Simplicity",
  description: "Automation shouldn't be complex. We design intuitive systems that respond to your presence without a second thought.",
  icon: BrainCircuit,
  link: "/AutomationRecipesPage",
  animation: "group-hover:text-secondary group-hover:scale-110"
}, {
  title: "Digital Zen",
  description: "Transform your living space into a sanctuary of calm with lighting and climate scenes that mirror your natural rhythm.",
  icon: BookOpen,
  link: "/MozaMethodPage",
  animation: "group-hover:text-secondary group-hover:scale-110"
}];
export default function CoreIntelligenceTracks() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223718" id="the-homekit-ecosystem" className="dark bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16">
          <h2 className="font-default text-3xl md:text-5xl font-bold mb-6 tracking-tight text-foreground">
            The HomeKit Ecosystem
          </h2>
          <p className="font-default text-muted-foreground max-w-2xl mx-auto text-lg">
            Experience the art of simple living through Apple's secure framework. Moza Casa brings technical precision to the warmth of your home.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tracks.map((track, i) => <Card key={i} data-index={i} className="group relative bg-card/20 backdrop-blur-md border-border/40 transition-all duration-500 hover:border-secondary/50 hover:shadow-2xl hover:shadow-white/5 overflow-hidden flex flex-col h-full">
              <CardHeader className="pb-4">
                <div className="mb-6 inline-flex p-4 rounded-xl bg-secondary/10 text-foreground transition-colors duration-500 group-hover:bg-secondary/20 w-fit">
                  <track.icon className={`w-8 h-8 transition-all duration-500 ease-out ${track.animation}`} />
                </div>
                <CardTitle className="font-default text-2xl tracking-tight text-foreground">
                  {track.title}
                </CardTitle>
              </CardHeader>
              
              <CardContent className="flex-grow pb-2">
                <CardDescription className="font-default text-base leading-relaxed text-muted-foreground/80 group-hover:text-foreground transition-colors duration-300">
                  {track.description}
                </CardDescription>
              </CardContent>

              <CardFooter className="pt-0">
                <div className="w-full grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-500 ease-in-out">
                  <div className="overflow-hidden">
                    <div className="pt-6 translate-y-4 group-hover:translate-y-0 transition-all duration-500 ease-out opacity-0 group-hover:opacity-100">
                      <Button asChild className="w-full group/btn border-secondary/30 hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 rounded-full" variant="outline">
                        <Link to={track.link}>
                          Learn More
                          <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}